import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core'
import { Color, ColorName } from '../design-system/colors/ColorPalette'
import { Icon, ICONS } from './icon.constants'

/**
 * Set of Design System icon sizes in pixels. For any other size, use {@link IconSizeCustom}.
 */
export const IconSizePxs = [16, 18, 20, 22, 24, 28] as const
export type IconSizePx = (typeof IconSizePxs)[number]

/**
 * An escape hatch for less common icon sizes. If a specific custom size is used often we can
 * consider moving it into the default set.
 *
 * @example
 * ``` html
 * <svg ftrIcon="More" [iconSize]="{ customPx: 64 }"></svg>
 * ```
 */
export interface IconSizeCustom {
  customPx: number
}

export type IconSize = IconSizePx | IconSizeCustom

/**
 * A lightweight SVG icon component that renders icons from the icon library. Supports custom
 * sizing and color theming from the design system palette. Falls back to `currentColor` for color
 * inheritance if no color is specified.
 *
 * @example
 * ``` html
 * <svg
 *   ftrIcon="More"
 *   [iconSize]="24"
 *   iconColor="Grey40"
 * ></svg>
 * ```
 */
@Component({
  selector: 'svg[ftrIcon]',
  template: '<svg:use [attr.href]="iconUrl()" />',
  styleUrl: './icon.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'img',
    '[attr.aria-hidden]': 'true',
    '[attr.data-icon]': 'ftrIcon()',
    '[attr.width]': 'iconSizeRem()',
    '[attr.height]': 'iconSizeRem()',
    '[attr.fill]': 'iconColorStyle()',
  },
})
export class IconComponent {
  /**
   * Name of the icon from the icon library.
   * @see {@link ICONS}
   */
  readonly ftrIcon = input.required<Icon>()

  /**
   * Width and height in pixels. Converts to rem internally. Use a value from the default
   * {@link IconSizePxs} set, or use {@link IconSizeCustom} for custom sizes.
   */
  readonly iconSize = input.required<IconSize>()

  /**
   * Optional name of fill color from the design system palette. Will use parent 'currentColor' if absent.
   * @see {@link Color}
   */
  readonly iconColor = input<ColorName>()

  protected readonly iconUrl = computed<string>(() => `#${ICONS[this.ftrIcon()]}`)

  protected readonly iconSizeRem = computed<string>(() => {
    const size = this.iconSize()
    const px = typeof size === 'number' ? size : size.customPx
    return `${px / 16}rem`
  })

  protected readonly iconColorStyle = computed<Color | 'currentColor'>(() => {
    const colorName = this.iconColor()
    return colorName ? Color[colorName] : 'currentColor'
  })
}
